import { Dispatch } from "redux";
import axios from "../../utils/axios.tsx";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import { loginAsAnonymousUser } from "../../store/auth.tsx";
import {
  conversationActions,
  fetchConversations,
} from "../../store/conversation.tsx";
import { BASE_URL } from "../../const.ts";

// Local type for streaming response data
interface StreamableResponse {
  content: string;
  progress_update: boolean;
  done: boolean;
  response_type: string;
}

interface ExchangeParams {
  conversation_id?: string;
  exchange_id?: string;
  prompt: string;
  model?: string;
}

interface AskQuestionParams {
  dispatch: Dispatch;
  isAuthenticated: boolean;
  stopAnimation: () => void;
  exchangeURL: string;
  streamSlug: string;
  exchangeParams: ExchangeParams;
}

export async function streamAsk({
  dispatch,
  isAuthenticated,
  stopAnimation,
  exchangeURL,
  streamSlug,
  exchangeParams,
}: AskQuestionParams): Promise<void> {
  const streamUrl = `${BASE_URL}${streamSlug}`;

  dispatch(searchFieldActions.setAwaitingAskResponse(true));
  const exchangeResponse = await axios.post(
    exchangeURL,
    JSON.stringify(exchangeParams),
    { withCredentials: true },
  );

  if (!isAuthenticated && exchangeResponse.status == 429) {
    const conversationId = exchangeParams.conversation_id || "new";
    dispatch(loginAsAnonymousUser());
    dispatch(
      conversationActions.appendCustomActionResponse({
        conversation_id: conversationId,
        prompt: exchangeParams.prompt,
        text: exchangeResponse.statusText,
        type: "TextResponse",
        savePrompt: true,
        progressUpdate: false,
      }),
    );
    dispatch(conversationActions.setSelectedConversation(conversationId));
    stopAnimation();
    dispatch(searchFieldActions.setAwaitingAskResponse(false));
    return;
  }
  if (!exchangeResponse || !exchangeResponse.data) {
    throw new Error("Failed to initiate streaming exchange.");
  }
  if (
    exchangeResponse.data.prompt === "NEW CONVERSATION" ||
    exchangeResponse.data.prompt.toUpperCase() === "!NC"
  ) {
    dispatch(conversationActions.resetExchanges());
    dispatch(conversationActions.loadAllExchanges([exchangeResponse.data]));
    dispatch(fetchConversations() as any);
    dispatch(
      conversationActions.setSelectedConversation(
        exchangeResponse.data.conversation_id,
      ),
    );
    stopAnimation();
    dispatch(searchFieldActions.setAwaitingAskResponse(false));
    return;
  }

  if (!isAuthenticated) {
    dispatch(
      conversationActions.setSelectedConversation(
        exchangeResponse.data.conversation_id,
      ),
    );
  }

  dispatch(
    conversationActions.appendExchange({
      conversation_id: exchangeResponse.data.conversation_id,
      id: exchangeResponse.data.id,
      prompt: exchangeResponse.data.prompt,
      response: null,
    }),
  );

  exchangeParams["conversation_id"] = exchangeResponse.data.conversation_id;

  try {
    if (!isAuthenticated) {
      dispatch(loginAsAnonymousUser());
    }

    // Step 2: Start the streaming using SSE hook approach
    const signedValue = exchangeResponse.data.signed_value;
    let streamUrlWithParams = `${streamUrl}?user_signature=${encodeURIComponent(signedValue)}`;
    let conversationId = exchangeParams["conversation_id"];
    if (conversationId) {
      streamUrlWithParams += `&conversation_id=${exchangeParams["conversation_id"]}`;
    }

    let accumulatedContent = "";
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE_MS = 100; // Throttle updates to every 100ms

    await new Promise<void>((resolve, reject) => {
      const eventSource = new EventSource(streamUrlWithParams);

      eventSource.onopen = () => {
        console.log("Streaming connection opened.");
      };

      eventSource.onmessage = (event) => {
        let askResponse;
        try {
          askResponse = JSON.parse(event.data);
          console.log(askResponse);
        } catch (error) {
          console.error("Error parsing SSE message:", error, event.data);
          return;
        }

        if (
          askResponse.type === "completed" ||
          askResponse.type === "stream_ended"
        ) {
          console.log("End of stream received.");
          // Final update with complete content
          if (accumulatedContent) {
            dispatch(
              conversationActions.appendExchange({
                conversation_id: exchangeResponse.data.conversation_id,
                response: accumulatedContent,
                type: "TextResponse",
                progress_update: false,
              }),
            );
          }
          eventSource.close();
          resolve();
        } else if (askResponse.type === "status") {
          const responseData = askResponse.data as StreamableResponse;

          try {
            if (responseData.progress_update) {
              if (responseData.done) {
                // Final completion - no more content accumulation needed
                return;
              } else {
                // Skip progress updates with empty content to prevent component unmounting
                if (
                  !responseData.content ||
                  responseData.content.trim() === ""
                ) {
                  console.log(
                    "🚫 Skipping empty progress update to prevent unmount",
                  );
                  return;
                }
                // Progress update - send as-is (client will handle dot animation)
                dispatch(
                  conversationActions.appendExchange({
                    conversation_id: exchangeResponse.data.conversation_id,
                    response: responseData.content,
                    type: responseData.response_type,
                    progress_update: true,
                  }),
                );
              }
            } else if (
              responseData.response_type === "Graph" ||
              responseData.response_type === "Table"
            ) {
              // Handle graphs/tables as complete objects - no accumulation
              const content = JSON.parse(responseData.content);
              dispatch(
                conversationActions.appendExchange({
                  conversation_id: exchangeResponse.data.conversation_id,
                  response: content,
                  type: responseData.response_type,
                  progress_update: false,
                }),
              );
            } else {
              // Text content - accumulate incremental chunks
              accumulatedContent += responseData.content;

              // Throttle updates to reduce flickering
              const now = Date.now();
              if (now - lastUpdateTime >= UPDATE_THROTTLE_MS) {
                dispatch(
                  conversationActions.appendExchange({
                    conversation_id: exchangeResponse.data.conversation_id,
                    response: accumulatedContent,
                    type: responseData.response_type,
                    progress_update: false,
                  }),
                );
                lastUpdateTime = now;
              }
            }
          } catch (error) {
            console.error("Error processing stream data:", error);
          }
        }
      };

      eventSource.onerror = (error) => {
        console.error("Streaming error:", error);
        eventSource.close();
        reject(error);
      };
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    stopAnimation();
    dispatch(searchFieldActions.setAwaitingAskResponse(false));
    if (isAuthenticated) {
      dispatch(fetchConversations() as any);
    }
  }
}
